import { useEffect, useState } from 'react';
import { effectivePermissionsSchema, type EffectivePermissionsWire } from '@forecastify/shared';
import { useSession } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The caller's own permission set, for UI gating.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **This is UX, not security.** CLAUDE.md rule 6: *"UI gating is UX; the server check is the only
 * security."* A client that lied about this response would gain nothing — every endpoint checks
 * again. What it buys is not offering a control that would be refused.
 *
 * ⚠️ **It lives at `screens/`, shared, because the second caller was about to copy it.** C7's
 * delivery route grew it first and C8's task route needs the same thing; two copies is how one
 * screen comes to gate on `task.update` while the other gates on `task.view` for the same button.
 */
export interface PermissionSet {
  /** Whether the caller holds an action at all — any scope but `none`. */
  readonly has: (action: string) => boolean;
  /** The scope, or `'none'`. A screen that needs to distinguish `own` from `workspace` asks this. */
  readonly scopeOf: (action: string) => string;
  readonly loaded: boolean;
}

export function usePermissionSet(): PermissionSet {
  const session = useSession();
  const [permissions, setPermissions] = useState<EffectivePermissionsWire | null>(null);

  useEffect(() => {
    /**
     * ⚠️ **A mutable OBJECT rather than a `let` flag.** With `let live = true`, TypeScript narrows
     * it to `true` inside the closure — it cannot see the cleanup reassigning it later — so the
     * guard reads as always-true and `no-unnecessary-condition` says so, correctly. Silencing the
     * rule would leave a guard the compiler believes is dead; a property read defeats the narrowing
     * honestly.
     *
     * The guard matters: without it a slow response for the previous workspace lands after a switch
     * and overwrites the current one's permissions.
     */
    const alive = { current: true };

    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/permissions/me').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = effectivePermissionsSchema.safeParse(await response.json());
      if (parsed.success && alive.current) setPermissions(parsed.data);
    })();

    return () => {
      alive.current = false;
    };
  }, [session]);

  return {
    loaded: permissions !== null,
    scopeOf: (action) => {
      if (permissions === null) return 'none';
      /* ⚠️ An ungranted action is ABSENT from the map, so "not present" has to mean `none` — a
       * check that only compared against `'none'` would read a missing key as permitted. */
      const scope: string | undefined = permissions.grants[action];
      return scope ?? 'none';
    },
    has: (action) => {
      if (permissions === null) return false;
      const scope: string | undefined = permissions.grants[action];
      return scope !== undefined && scope !== 'none';
    },
  };
}

/**
 * Parse a `{ data: [...] }` envelope with a shared element schema.
 *
 * ⚠️ **Without importing zod.** `apps/web` does not depend on it — only `@forecastify/shared` does
 * — and adding a direct dependency to write one wrapper puts a second copy of zod in the browser
 * bundle the first time the two versions drift.
 *
 * ⚠️ **Still a real parse, not a cast.** Every element goes through the shared schema; only the
 * `{ data: [...] }` shell is hand-written. A response whose `data` is missing, is not an array, or
 * holds one bad row is rejected here rather than becoming `undefined` inside a render.
 */
export function parseList<T>(
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
  body: unknown,
): T[] | null {
  if (typeof body !== 'object' || body === null) return null;
  const data = (body as { data?: unknown }).data;
  if (!Array.isArray(data)) return null;

  const parsed: T[] = [];
  for (const entry of data) {
    const result = schema.safeParse(entry);
    if (!result.success || result.data === undefined) return null;
    parsed.push(result.data);
  }
  return parsed;
}

/**
 * The same, for a single resource.
 *
 * ⚠️ **`{ data: <resource> }` is the API's shape for one thing** — CLAUDE.md §3 — and every screen
 * that reads one was unwrapping it by hand. Same argument as `parseList`: the shell is
 * hand-written, the body is a real parse, and a bad row becomes `null` here rather than
 * `undefined` inside a render.
 */
export function parseOne<T>(
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
  body: unknown,
): T | null {
  if (typeof body !== 'object' || body === null) return null;
  const result = schema.safeParse((body as { data?: unknown }).data);
  return result.success && result.data !== undefined ? result.data : null;
}

/**
 * The `meta.next_cursor` of a list envelope, or `null`.
 *
 * ⚠️ **Beside `parseList` because they read the same body and neither should re-parse it.** A list
 * response is `{ data, meta }`; `parseList` takes the first half and this takes the second, so a
 * caller does `await response.json()` once and asks both.
 *
 * ⚠️ **Absent means the last page**, and so does a malformed one. A cursor is opaque and the client
 * never composes one — anything that is not a string is *there is no next page*, which fails by
 * disabling a button rather than by paging into nothing.
 */
/**
 * The `meta.total` of a list envelope, or `0`.
 *
 * ⚠️⚠️ **`0` RATHER THAN A THROW WHEN IT IS ABSENT, AND `0` IS THE HONEST ANSWER.** The pager reads
 * *page 1 of 1* from it, which is what a list whose server did not count should show — the
 * alternative is a made-up figure under a control somebody navigates by.
 *
 * ⚠️ **The number counts what the FILTERS left**, not what the table holds; `pagination.ts` argues
 * why, and the label the screen puts beside it has to say so.
 */
export function totalOf(body: unknown): number {
  if (typeof body !== 'object' || body === null) return 0;
  const meta = (body as { meta?: unknown }).meta;
  if (typeof meta !== 'object' || meta === null) return 0;
  const total = (meta as { total?: unknown }).total;
  return typeof total === 'number' && Number.isFinite(total) && total >= 0 ? total : 0;
}

export function nextCursorOf(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const meta = (body as { meta?: unknown }).meta;
  if (typeof meta !== 'object' || meta === null) return null;
  const cursor = (meta as { next_cursor?: unknown }).next_cursor;
  return typeof cursor === 'string' && cursor !== '' ? cursor : null;
}

/** Who is reading — the member row of the session's current workspace, or `''` before it loads. */
export function useMyMemberId(): string {
  const session = useSession();
  return (
    session.workspaces.find((workspace) => workspace.id === session.workspaceId)?.memberId ?? ''
  );
}

/**
 * 22 Oct 2026 · whether `action` reaches THIS project: every project at `workspace`, and at `own`
 * only one the reader is PM of — the server's rule (`project-manage.ts`), so a screen offers no
 * edit it would refuse.
 */
export function managesProject(
  permissions: PermissionSet,
  action: 'project.manage' | 'contract.manage' | 'lineitem.manage',
  pmMemberId: string | null,
  memberId: string,
): boolean {
  const scope = permissions.scopeOf(action);
  return (
    scope === 'workspace' || (scope === 'own' && pmMemberId !== null && pmMemberId === memberId)
  );
}
